import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OrdersAPI } from '../services/api';

export default function OrderDetail({ embeddedOrderId, onClose }) {
  const params = useParams();
  const routeId = params?.id;
  const id = embeddedOrderId ?? routeId;
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await OrdersAPI.get(id);
        setOrder(data);
        setError('');
      } catch (e) {
        setError(e?.response?.data?.error || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const formatINR = (val) => {
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  if (loading) return <div className="page-content"><div style={{ padding:16 }}>Loading…</div></div>;
  if (error) return <div className="page-content"><div style={{ padding:16, color:'#b91c1c' }}>{error}</div></div>;
  if (!order) return null;

  const ship = order?.shippingAddress || {};
  const addressParts = [ship.addressLine1, ship.addressLine2, ship.city, ship.state, ship.pincode].filter(Boolean);
  const addressText = ship.resolvedAddress || (addressParts.length ? addressParts.join(', ') : '');

  const getItemDisplayName = (it) =>
    it?.name ||
    it?.item?.name ||
    it?.itemSnapshot?.name ||
    it?.itemName ||
    it?.description ||
    'Item';

  // Prefer invoice items; fall back to quote items (public order created quotes first)
  const invoiceItems = Array.isArray(order?.invoice?.items) ? order.invoice.items : [];
  const quoteItems = Array.isArray(order?.quote?.items) ? order.quote.items : [];
  const items = invoiceItems.length > 0 ? invoiceItems : quoteItems;

  const labelNumber = order?.invoice?.number || (order?.quote?.number ? `QUOTE ${order.quote.number}` : (order.invoiceId ? `INV${order.invoiceId}` : '-'));

  return (
    <>
      <style>{`
        @media print {
          /* Hide everything first, then reveal only the ticket area */
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area { position: absolute !important; left: 0 !important; top: 0 !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-area { display: block !important; }
          header, nav, .navbar, .site-header, .app-header, .main-header, footer, .footer { display: none !important; }
          @page { size: 80mm auto; margin: 5mm; }
          html, body { width: 80mm; }
          .bill { width: 80mm; font-size: 12px; color: #111827; }
          .bill h1, .bill h2, .bill h3 { margin: 0; }
          .bill hr { border: none; border-top: 1px dashed #9ca3af; margin: 6px 0; }
          .bill table { width: 100%; border-collapse: collapse; }
          .bill th, .bill td { padding: 2mm 0; font-size: 12px; }
          .bill th { text-align: left; }
          .bill td.qty { text-align: right; }
        }
        @media screen {
          .print-area { display: none; }
        }
      `}</style>

      <div className="no-print page-content">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <h2 style={{ margin:0 }}>Order #{order.number || `ORD${order.id}`}</h2>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
            {onClose ? (
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            ) : (
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>Back</button>
            )}
          </div>
        </div>

        <div className="form-section" style={{ maxWidth: 960 }}>
          <div className="form-row">
            <div className="form-group">
              <label>Customer</label>
              <div style={{ fontWeight:600 }}>{order?.customer?.name || '-'}</div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <div style={{ fontWeight:600 }}>{order?.status || '-'}</div>
            </div>
          {/*   <div className="form-group">
              <label>Document</label>
              <div style={{ fontWeight:600 }}>{labelNumber}</div>
            </div> */}
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex:1 }}>
              <label>Shipping Address</label>
              <div>{addressText || '-'}</div>
             {/*  {!!ship?.location && (
                <div style={{ color:'#6b7280', marginTop:4 }}>Lat: {ship.location.lat ?? '-'}, Lng: {ship.location.lng ?? '-'}</div>
              )} */}
            </div>
          </div>

          {items.length > 0 && (
            <div style={{ marginTop:12 }}>
              <div style={{ fontWeight:600, marginBottom:6 }}>Items</div>
              <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#f9fafb' }}>
                    <th style={{ textAlign:'left', padding:'6px' }}>Name</th>
                    <th style={{ textAlign:'left', padding:'6px' }}>Description</th>
                    <th style={{ textAlign:'right', padding:'6px' }}>Quantity</th>
                    <th style={{ textAlign:'left', padding:'6px' }}>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} style={{ borderTop:'1px solid #e5e7eb' }}>
                      <td style={{ padding:'6px' }}>{getItemDisplayName(it)}</td>
                      <td style={{ padding:'6px' }}>{it.description}</td>
                      <td style={{ padding:'6px', textAlign:'right' }}>{it.quantity}</td>
                      <td style={{ padding:'6px' }}>{it.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>Notes: {order?.notes || '-'}</div>
            </div>
          )}

        </div>
      </div>

      <div className="print-area">
        <div className="bill" style={{ margin:'0 auto' }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:14 }}>Order #{order.number || `ORD${order.id}`}</div>
            <div style={{ fontSize:12, marginTop:2 }}>{order?.customer?.name || '-'}</div>
            {!!addressText && <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{addressText}</div>}
            <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>Notes: {order?.notes || '-'}</div>
          </div>
          <hr />
          {items.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ textAlign:'right' }}>Qty</th>
                  <th style={{ textAlign:'left', paddingLeft:6 }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{getItemDisplayName(it)}</td>
                    <td className="qty">{it?.quantity}</td>
                    <td style={{ paddingLeft:6 }}>{it?.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <hr />
          <div style={{ textAlign:'center', fontSize:11 }}>Thank you!</div>
        </div>
      </div>
    </>
  );
}


